import {
  ComponentType,
  DataType,
  AssetDataSource,
} from '@vgtv/api-client/lib/component';

import type {
  AssetListComponent,
  CategoryListComponent,
  ListComponent,
  SvpAssetListComponent,
  SvpAssetPresetListComponent,
  SvpCategoryListComponent,
  SvpCategoryPresetListComponent,
  SvpMostSeenListComponent,
  SvpSearchListComponent,
  MixedListComponent,
  CurateBlendListComponent,
} from '@vgtv/api-client/lib/component/list';
import type {
  AssetSliderComponent,
  CategorySliderComponent,
  ProfilePresetSliderComponent,
  SliderComponent,
  SvpAssetPresetSliderComponent,
  SvpAssetSliderComponent,
  SvpCategoryPresetSliderComponent,
  SvpEpisodeSliderComponent,
  SvpMostSeenSliderComponent,
  SvpSearchSliderComponent,
  SvpSeasonSliderComponent,
  MixedSliderComponent,
  CurateBlendSliderComponent,
} from '@vgtv/api-client/lib/component/slider';
import type { ContinueWatchingComponent } from '@vgtv/api-client/lib/component/continue_watching';
import type {
  SvpCategory,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type { Component } from '@vgtv/api-client/lib/component';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';
import type { FilteredListComponent } from '@vgtv/api-client/lib/component/filtered_list';
import type { SearchComponent } from '../../modules/search';

/**
 * Partial intersection of a couple of components types, makes it easier to calculate the context of an asset
 */
export type AssetContextSource = { categoryId?: SvpCategoryId } & (
  | Pick<
      | SvpAssetListComponent
      | SvpAssetSliderComponent
      | SvpSearchListComponent
      | SvpSearchSliderComponent
      | SvpMostSeenListComponent
      | SvpMostSeenSliderComponent
      | SvpAssetPresetListComponent
      | ContinueWatchingComponent
      | SvpSeasonSliderComponent
      | SvpCategoryListComponent
      | SvpCategoryPresetListComponent
      | SvpEpisodeSliderComponent
      | MixedSliderComponent
      | MixedListComponent
      | CurateBlendListComponent
      | CurateBlendSliderComponent,
      'data' | 'type'
    >
  | { type: ComponentType.FILTERED_LIST }
);

export function isListComponent(
  component: Component
): component is ListComponent<true> {
  return component.type === ComponentType.LIST;
}

export function isAssetListComponent(
  component: Component
): component is AssetListComponent<true> {
  return isListComponent(component) && component.data.type === DataType.ASSETS;
}

export function isCategoryListComponent(
  component: Component
): component is CategoryListComponent<true> {
  return (
    isListComponent(component) && component.data.type === DataType.CATEGORIES
  );
}

export function isSliderComponent(
  component: Component | SvpEpisodeSliderComponent
): component is SliderComponent | SvpEpisodeSliderComponent {
  return component.type === ComponentType.SLIDER;
}

export function isAssetSliderComponent(
  component: Component | SvpEpisodeSliderComponent
): component is AssetSliderComponent {
  return (
    isSliderComponent(component) && component.data.type === DataType.ASSETS
  );
}

export function isSvpEpisodeSliderComponent(
  component: Component | SvpEpisodeSliderComponent
): component is SvpEpisodeSliderComponent {
  return (
    isSliderComponent(component) &&
    component.data.type === DataType.ASSETS &&
    component.data.source?.endpoint === AssetDataSource.SVP_EPISODES
  );
}

export function isCategorySliderComponent(
  component: Component | SvpEpisodeSliderComponent
): component is CategorySliderComponent {
  return (
    isSliderComponent(component) && component.data.type === DataType.CATEGORIES
  );
}

export function isFilteredListComponent(
  component: Component
): component is FilteredListComponent {
  return component.type === ComponentType.FILTERED_LIST;
}

export function isSeasonSliderComponent(
  component: Component
): component is SvpSeasonSliderComponent {
  return (
    isSliderComponent(component) && component.data.type === DataType.SEASONS
  );
}

export function isContinueWatchingComponent(
  component: Component | SvpEpisodeSliderComponent
): component is ContinueWatchingComponent {
  return component.type === ComponentType.CONTINUE_WATCHING;
}

export function isComponentWithPreset(
  component: Component
): component is
  | SvpAssetPresetListComponent
  | SvpCategoryPresetListComponent
  | SvpAssetPresetSliderComponent
  | SvpCategoryPresetSliderComponent
  | ProfilePresetSliderComponent {
  return (
    (isListComponent(component) || isSliderComponent(component)) &&
    Boolean(component.data.preset?.length ?? 0 > 0)
  );
}

export function isBlenderDataSourceComponent(
  component: Component | SvpEpisodeSliderComponent
): component is CurateBlendListComponent | CurateBlendSliderComponent {
  if (isSvpEpisodeSliderComponent(component)) {
    return false;
  }

  return (
    (isListComponent(component) || isSliderComponent(component)) &&
    component.data.source?.endpoint === AssetDataSource.CURATE_BLEND
  );
}

export function getComponentCategoryId(component: Component) {
  if (
    isComponentWithPreset(component) ||
    isBlenderDataSourceComponent(component)
  ) {
    return undefined;
  }

  if (
    isSeasonSliderComponent(component) ||
    isFilteredListComponent(component)
  ) {
    return component.categoryId;
  }

  if (
    isAssetListComponent(component) ||
    isAssetSliderComponent(component) ||
    isContinueWatchingComponent(component)
  ) {
    const componentId = component.data.source?.params?.filter?.categoryId;
    if (componentId) {
      return Number(componentId) as SvpCategory['id'];
    }
  }
}

export function getAssetContextFromComponent(component?: AssetContextSource) {
  if (!component) {
    return null;
  }
  const context: {
    componentType?: ComponentType;
    dataType: DataType;
    categoryId?: SvpCategory['id'];
    tagId?: CnpTag['id'][];
    storyId?: CnpStory['id'];
  } = {
    componentType: component?.type,
    dataType: DataType.ASSETS,
    categoryId: getComponentCategoryId(component as Component),
  };

  if (component?.type !== ComponentType.FILTERED_LIST && component?.data) {
    context.dataType = component.data.type;

    if (component.type === ComponentType.LIST) {
      const listFilter = (component.data as SvpAssetListComponent['data'])
        .source?.params?.filter;

      context.tagId = listFilter?.tag;
      context.storyId = listFilter?.story;
    }
  }

  return context;
}

export const getAnalyticsConfig = (component: Component) => {
  if ('analytics' in component) {
    const { analytics } = component as SearchComponent;

    return analytics || {};
  }

  return {};
};
